import { MARK_STROKE_PT } from "@/lib/feedback-marks";
import type { AnnotationKind, PointPt, RectPt, ShapeTarget } from "@/lib/protocol";
import type { TextLayerData, TextWord } from "@/lib/text-layer";

type ShapeKind = Exclude<AnnotationKind, "text">;

function normalizedRect([x1, y1, x2, y2]: RectPt): RectPt {
  return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
}

function rectsIntersect(a: RectPt, b: RectPt): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

function wordRect([x1, y1, x2, y2]: TextWord): RectPt {
  return normalizedRect([x1, y1, x2, y2]);
}

function ellipseIntersectsRect(rect: RectPt, word: RectPt): boolean {
  const [x1, y1, x2, y2] = normalizedRect(rect);
  const centreX = (x1 + x2) / 2;
  const centreY = (y1 + y2) / 2;
  const radiusX = (x2 - x1) / 2;
  const radiusY = (y2 - y1) / 2;
  if (radiusX === 0 || radiusY === 0) return rectsIntersect([x1, y1, x2, y2], word);
  const nearestX = Math.max(word[0], Math.min(centreX, word[2]));
  const nearestY = Math.max(word[1], Math.min(centreY, word[3]));
  return ((nearestX - centreX) / radiusX) ** 2 + ((nearestY - centreY) / radiusY) ** 2 <= 1;
}

function segmentIntersectsRect(start: PointPt, end: PointPt, rect: RectPt, padding: number): boolean {
  const left = rect[0] - padding;
  const top = rect[1] - padding;
  const right = rect[2] + padding;
  const bottom = rect[3] + padding;
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  let first = 0;
  let last = 1;
  for (const [origin, delta, low, high] of [[start[0], dx, left, right], [start[1], dy, top, bottom]] as const) {
    if (delta === 0) {
      if (origin < low || origin > high) return false;
      continue;
    }
    const a = (low - origin) / delta;
    const b = (high - origin) / delta;
    first = Math.max(first, Math.min(a, b));
    last = Math.min(last, Math.max(a, b));
    if (first > last) return false;
  }
  return true;
}

function strokePadding(target: ShapeTarget): number {
  return (target.stroke?.width ?? MARK_STROKE_PT) / 2;
}

function paintsWord(kind: ShapeKind, target: ShapeTarget, word: RectPt): boolean {
  if (kind === "region" || kind === "rect") return rectsIntersect(normalizedRect(target.rect), word);
  if (kind === "ellipse") return ellipseIntersectsRect(target.rect, word);
  if (kind === "line" || kind === "arrow") {
    const points = target.points;
    return points !== undefined && segmentIntersectsRect(points[0], points[1], word, strokePadding(target));
  }
  const path = target.path ?? [];
  for (let index = 1; index < path.length; index += 1) {
    if (segmentIntersectsRect(path[index - 1]!, path[index]!, word, strokePadding(target))) return true;
  }
  return false;
}

export function coveredWords(kind: ShapeKind, target: ShapeTarget, layer: TextLayerData): string | null {
  const words = layer.words
    .filter((word) => paintsWord(kind, target, wordRect(word)))
    .map(([, , , , start, end]) => layer.text.slice(start, end).replace(/\s+/gu, " ").trim())
    .filter(Boolean);
  return words.length ? words.join(" ") : null;
}
